import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EmptyState, Notice, PageHeader } from '@wodi/ui';

import { sessionQuery } from '../api/session.ts';
import {
  MOH_FORMS,
  reportGroupsFor,
  type MohFormKey,
  type ReportEntry,
} from '../reports/catalogue.ts';
import { BackToReports } from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

/** The reports index (plan §9.1): only what this person may open, in plain groups. */
export function ReportsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const groups = session === null ? [] : reportGroupsFor(session);
  return (
    <div className={styles.page}>
      <PageHeader title="Reports" />
      {groups.length === 0 ? (
        <EmptyState>No reports for your role. Ask an administrator if you need one.</EmptyState>
      ) : null}
      {groups.map((group) => {
        const id = `reports-${group.title.toLowerCase().replace(/[^a-z]+/g, '-')}`;
        return (
          <section key={group.title} className={styles.section} aria-labelledby={id}>
            <h2 id={id} className={styles.groupHeading}>
              {group.title}
            </h2>
            <ul className={styles.list}>
              {group.entries.map((entry) => (
                <li key={entry.title} className={styles.listRow}>
                  <EntryTitle entry={entry} />
                  <span>
                    {entry.description}
                    {entry.waitsFor === undefined ? null : (
                      <span className={styles.secondLine}>{entry.waitsFor}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function EntryTitle({ entry }: { entry: ReportEntry }) {
  if (entry.href === null) return <span>{entry.title}</span>;
  if (entry.href === '/reports/moh/$form' && entry.params !== undefined && 'form' in entry.params)
    return (
      <Link to="/reports/moh/$form" params={{ form: entry.params.form }}>
        {entry.title}
      </Link>
    );
  if (
    entry.href === '/reports/analytics/$area' &&
    entry.params !== undefined &&
    'area' in entry.params
  )
    return (
      <Link to="/reports/analytics/$area" params={{ area: entry.params.area }}>
        {entry.title}
      </Link>
    );
  return <Link to={entry.href}>{entry.title}</Link>;
}

/**
 * An MOH return (plan §4.1, R1). The official 705A, 705B and 717 definitions haven't been
 * supplied, so the page says so and shows nothing approximate in their place.
 * TODO(verify-spec): R1–R8, R12 — build the run screens once a reviewed definition exists.
 */
export function MohFormPage({ form }: { form: string }) {
  const known = Object.hasOwn(MOH_FORMS, form) ? MOH_FORMS[form as MohFormKey] : null;
  if (known === null)
    return (
      <div className={styles.page}>
        <BackToReports />
        <PageHeader title="Report not found" />
        <EmptyState>There is no MOH report at this address.</EmptyState>
      </div>
    );
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title={known.title} />
      <p className={styles.summary}>{known.description}</p>
      <Notice tone="info">
        {`${known.title} isn't available yet. Its official definition hasn't been loaded and reviewed, so Wodi doesn't prepare it. Keep using the paper form until it is.`}
      </Notice>
    </div>
  );
}
